import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { BiDownload } from 'react-icons/bi';
import { saveAs } from 'file-saver';
import FormService from '../../../../core/service/FormService';
import {
	buildDceQuestionPermissionRows,
	buildNestedSectionGroups,
	countQuestionsInSkeleton,
	filterRowsBySearchQuery
} from '../../../../core/utilities/dceQuestionPermissionRows';
import { buildDceQuestionPermissionsExcelBuffer } from '../../../../core/utilities/dceQuestionPermissionsExcelExport';
import './DCEQuestionPermissionsModal.scss';

function DCEQuestionPermissionsModal({ t, show, assessmentId, assessmentLabel, onHide }) {
	const [rows, setRows] = useState([]);
	const [loadError, setLoadError] = useState(null);
	const [loading, setLoading] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [previewQuestionCount, setPreviewQuestionCount] = useState(null);

	useEffect(() => {
		if (!show || !assessmentId) return;

		let cancelled = false;
		setLoadError(null);
		setRows([]);
		setPreviewQuestionCount(null);
		setLoading(true);

		FormService.getAssessment(assessmentId, { fetchAll: true })
			.then((assessmentRes) => {
				if (cancelled || assessmentRes.status !== 200) return;
				const formId = assessmentRes.data?.assessmentForm?.id;
				const perms = assessmentRes.data?.formPermissions || [];
				if (!formId) {
					setLoadError(t('DCEQuestionPermissions.error.label'));
					return;
				}
				return FormService.getFormPreview(formId, false).then((previewRes) => {
					if (cancelled) return;
					if (previewRes.status !== 200) {
						setLoadError(t('DCEQuestionPermissions.error.label'));
						return;
					}
					const tLevel = (level) => t('AssessmentFormModal.approverLevel.label', { level });
					const built = buildDceQuestionPermissionRows(
						previewRes.data,
						perms,
						tLevel
					);
					if (cancelled) return;
					setPreviewQuestionCount(countQuestionsInSkeleton(previewRes.data));
					setRows(built);
				});
			})
			.catch(() => {
				if (!cancelled) setLoadError(t('DCEQuestionPermissions.error.label'));
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [show, assessmentId, t]);

	useEffect(() => {
		if (!show) setSearchQuery('');
	}, [show]);

	const displayRows = useMemo(
		() => filterRowsBySearchQuery(rows, searchQuery),
		[rows, searchQuery]
	);

	const nestedGroups = useMemo(
		() => buildNestedSectionGroups(displayRows),
		[displayRows]
	);

	const handleDownloadExcel = useCallback(async () => {
		if (!displayRows.length) return;

		const headerRow = [
			t('DCEQuestionPermissions.excel.column.section.label'),
			t('DCEQuestionPermissions.excel.column.subsection.label'),
			t('DCEQuestionPermissions.excel.column.question.label'),
			t('DataOwners.label'),
			t('Approvers.label')
		];
		const rawSheetName = t('DCEQuestionPermissions.excel.sheetName.label');
		const sheetName = rawSheetName.slice(0, 31);
		const tApproverLevel = (level) => t('AssessmentFormModal.approverLevel.label', { level });

		const base =
			(assessmentLabel || `DCE_${assessmentId}`)
				.replace(/[\\/:*?"<>|]+/g, '_')
				.replace(/\s+/g, '_')
				.slice(0, 72) || 'DCE';
		const dateStr = new Date().toISOString().slice(0, 10);
		const fileName = `${base}_QuestionPermissions_${dateStr}.xlsx`;

		try {
			const buffer = await buildDceQuestionPermissionsExcelBuffer(
				displayRows,
				headerRow,
				sheetName,
				tApproverLevel
			);
			const blob = new Blob([buffer], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
			});
			saveAs(blob, fileName);
		} catch (err) {
			console.error('DCE question permissions Excel export failed', err);
		}
	}, [assessmentId, assessmentLabel, displayRows, t]);

	const renderQuestionCell = (row) => (
		<div className="dce-qperm-question-inner" data-testid="dce-qperm-question-cell">
			<span className="dce-qperm-badge dce-qperm-badge--question">{t('DCEQuestionPermissions.badge.question.label')}</span>
			<span className="dce-qperm-question-text">{row.questionName}</span>
		</div>
	);

	const renderOwnersCell = (row) => {
		if (!row.blocks?.length) {
			return <span className="text-muted">—</span>;
		}
		return (
			<div className="dce-qperm-cell-blocks">
				{row.blocks.map((b) => (
					<div key={`${b.type}-${b.id}`} className="dce-qperm-block">
						{b.type === 'group' && b.name && (
							<div className="dce-qperm-block-title">{b.name}</div>
						)}
						{b.ownerLines?.length > 0 ? (
							b.ownerLines.map((line, idx) => (
								<div key={`o-${idx}`} className="dce-qperm-line">
									{line}
								</div>
							))
						) : (
							<span className="text-muted">—</span>
						)}
					</div>
				))}
			</div>
		);
	};

	const renderApproversCell = (row) => {
		if (!row.blocks?.length) {
			return <span className="text-muted">—</span>;
		}
		const blocksWithApprovers = row.blocks.filter((b) => b.approverLines?.length > 0);
		if (blocksWithApprovers.length === 0) {
			return <span className="text-muted">—</span>;
		}
		return (
			<div className="dce-qperm-cell-blocks">
				{blocksWithApprovers.map((b) => (
					<div key={`a-${b.type}-${b.id}`} className="dce-qperm-block">
						{b.approverLines.map((a, idx) => (
							<div key={`ap-${idx}`} className="dce-qperm-approver-level">
								{a.emails?.length > 0 ? (
									<>
										<div className="dce-qperm-approver-level-label">
											{t('DCEQuestionPermissions.approverLevelHeading.label', { level: a.level })}
										</div>
										{a.emails.map((email, eidx) => (
											<div key={`e-${eidx}`} className="dce-qperm-line">
												{email}
											</div>
										))}
									</>
								) : (
									<div className="dce-qperm-line">{a.label}</div>
								)}
							</div>
						))}
					</div>
				))}
			</div>
		);
	};

	return (
		<Modal
			show={show}
			onHide={onHide}
			size="fullscreen"
			centered
			className="dce-qperm-modal"
			data-testid="dce-question-permissions-modal"
		>
			<Modal.Header closeButton>
				<Modal.Title as="h5">
					{t('DCEQuestionPermissions.modalTitle.label')}
					{assessmentLabel ? (
						<span className="dce-qperm-subtitle text-muted ms-2">{assessmentLabel}</span>
					) : null}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{loadError && <div className="alert alert-danger">{loadError}</div>}
				{loading && (
					<div className="text-center py-5" data-testid="dce-qperm-loading">
						<Spinner animation="border" role="status" variant="primary" />
						<div className="mt-2 text-muted">{t('DCEQuestionPermissions.loading.label')}</div>
					</div>
				)}
				{!loading && !loadError && rows.length === 0 && (
					<div className="text-muted text-center py-3" data-testid="dce-qperm-empty">
						{previewQuestionCount > 0
							? t('DCEQuestionPermissions.emptyNoOwners.label')
							: t('DCEQuestionPermissions.empty.label')}
					</div>
				)}
				{!loading && !loadError && rows.length > 0 && (
					<>
						<div className="dce-qperm-toolbar mb-3">
							<div className="dce-qperm-toolbar-search">
								<Form.Group controlId="dce-qperm-search">
									<Form.Label className="small text-muted mb-1">
										{t('DCEQuestionPermissions.search.label')}
									</Form.Label>
									<Form.Control
										type="search"
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										placeholder={t('DCEQuestionPermissions.search.placeholder.label')}
										data-testid="dce-qperm-search"
										autoComplete="off"
									/>
								</Form.Group>
							</div>
							<div className="dce-qperm-toolbar-actions">
								<Button
									variant="outline-primary"
									size="sm"
									className="dce-qperm-excel-btn"
									disabled={displayRows.length === 0}
									onClick={handleDownloadExcel}
									data-testid="dce-qperm-download-excel"
								>
									<BiDownload className="me-1" aria-hidden />
									{t('DCEQuestionPermissions.downloadExcel.label')}
								</Button>
							</div>
						</div>
						{displayRows.length === 0 ? (
							<div className="text-muted text-center py-3" data-testid="dce-qperm-no-owner-match">
								{t('DCEQuestionPermissions.noOwnerMatch.label')}
							</div>
						) : (
							<div className="table-responsive dce-qperm-table-wrap">
								<Table bordered hover size="sm" className="dce-qperm-table mb-0">
									<thead>
										<tr>
											<th className="dce-qperm-col-hierarchy">
												{t('DCEQuestionPermissions.column.questionHierarchy.label')}
											</th>
											<th className="dce-qperm-col-owners">{t('DataOwners.label')}</th>
											<th className="dce-qperm-col-approvers">{t('Approvers.label')}</th>
										</tr>
									</thead>
									<tbody>
										{nestedGroups.map((sec, secIdx) => (
											<React.Fragment key={`sec-${sec.sectionTitle}-${secIdx}`}>
												<tr
													className="dce-qperm-section-heading"
													data-testid={`dce-qperm-section-${secIdx}`}
												>
													<td colSpan={3}>
														<div className="dce-qperm-heading-inner">
															<span className="dce-qperm-badge dce-qperm-badge--section">
																{t('DCEQuestionPermissions.badge.section.label')}
															</span>
															<span className="dce-qperm-heading-title">{sec.sectionTitle}</span>
														</div>
													</td>
												</tr>
												{sec.subGroups.map((sub, subIdx) => (
													<React.Fragment key={`sec-${secIdx}-sub-${sub.subKey}-${subIdx}`}>
														{sub.subSectionTitles.length > 0 && (
															<tr
																className="dce-qperm-subsection-heading"
																data-testid={`dce-qperm-subsection-${secIdx}-${subIdx}`}
															>
																<td colSpan={3}>
																	<div className="dce-qperm-heading-inner dce-qperm-heading-inner--subsection">
																		<span className="dce-qperm-badge dce-qperm-badge--subsection">
																			{t('DCEQuestionPermissions.badge.subsection.label')}
																		</span>
																		<span className="dce-qperm-heading-title">{sub.subSectionTitles.join(' › ')}</span>
																	</div>
																</td>
															</tr>
														)}
														{sub.rows.map((row) => (
															<tr
																key={row.fieldId}
																className="dce-qperm-question-row"
																data-testid={`dce-qperm-row-${row.fieldId}`}
															>
																<td>{renderQuestionCell(row)}</td>
																<td>{renderOwnersCell(row)}</td>
																<td>{renderApproversCell(row)}</td>
															</tr>
														))}
													</React.Fragment>
												))}
											</React.Fragment>
										))}
									</tbody>
								</Table>
							</div>
						)}
					</>
				)}
			</Modal.Body>
		</Modal>
	);
}

export default withTranslation()(DCEQuestionPermissionsModal);
